import { useContext, useMemo } from "react";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { BaseWidget } from "./BaseWidget";
import { ResearchWidget } from "./ResearchWidget";
import { NestedItemControl } from "../controls/NestedItemControl";
import { SelectControl, SelectControlRenderer } from "../controls/SelectControl";
import { useFirestoreGeneral, useFirestoreGeneralOrderBy } from "../../../../firebase/useFirestore";
import useMatrixData from "../../../data/useMatrixData";
import useWidgetPersona from "./_shared/useWidgetPersona";
import usePublicPersona from "./_shared/usePublicPersona";
import templatePersonaNotice from "./_shared/templatePersonaNotice";
import { TemplateDataContext } from "../context/TemplateDataContext";
import orderResearchChoices from "./_shared/orderResearchChoices";
import NO_PERSONA_TEXT from "./_shared/noPersonaText";
import useWidgetPeriod from "./_shared/useWidgetPeriod";
import ModulePlaceholder from "./_shared/ModulePlaceholder";
import WidgetSourcePlaceholder from "../components/WidgetSourcePlaceholder";
import ResearchFieldHeading from "../../../dataVisualizations/ResearchFieldHeading";
import LoadingSpinner from "../../../common/LoadingSpinner";
import isQuestionField from "../../../../helpers/questionnaire/isQuestionField";
import { supportsTotalGrowth } from "../../../../helpers/visualisationDefaults";

// AI-CONTEXT: See docs/features/livereport-per-deelnemer.md (T8)
// 'Groei op metingen' — per gekozen vraag van één onderzoek de score van de
// deelnemer van het rapport per meetmoment, naast elkaar. Nodig omdat de
// onderzoekwidget een grafiek over de hele groep tekent en geen getallen per
// meetmoment voor één persoon.
//
// Geen eigen groeiformule: de score komt uit useMatrixData (dezelfde route als
// de matrixgrafieken: koppeling op RowID, negatieve stellingen gespiegeld,
// meetmomenten op MomentPosition). Zelf rekenen op `Number(r.Input)` slaat de
// spiegeling over.
// AI-CONTEXT: See docs/pitfalls.md#growth-dual-calculation
// AI-CONTEXT: See docs/pitfalls.md#database-raw-input-skips-negative-mirroring
//
// Op de openbare link leest dezelfde route via PublicReportContext; de server
// zet daar de deelnemer van het rapport en laat PERSONA_GROWTH toe zoals RESEARCH
// (functions/liveReports/publicReportDataAccess.js).

const NOT_SCALE_NOTE = 'Dit is geen vraag met stellingen op een schaal, dus er is geen score per meetmoment.';

/**
 * De onderzoekkeuze van de onderzoekwidget, met de onderzoeken waarin de
 * deelnemer van het rapport antwoorden heeft bovenaan (B1, /test 2026-10-20).
 * Nodig omdat SelectControl zijn keuzes één keer laadt, zonder de deelnemer
 * te kennen; die staat alleen in de React-context van het rapport.
 *
 * @extends {SelectControl}
 */
class PersonaResearchSelectControl extends SelectControl {
    /** @type {SelectControl['render']} */
    render(value, onChange) {
        return <PersonaResearchSelectRenderer control={this} value={value} onChange={onChange} />;
    }
}

/**
 * Eén query op de antwoorden van de deelnemer, niet één per onderzoek
 * (risico 11). Zonder deelnemer is de id `''` en draait er geen query.
 *
 * @param {object} props
 * @param {SelectControl} props.control - De onderzoekkeuze
 * @param {string} props.value - Gekozen Research `ID`
 * @param {(value: string) => void} props.onChange - Bij een andere keuze
 * @returns {JSX.Element}
 */
const PersonaResearchSelectRenderer = ({ control, value, onChange }) => {
    const personaId = useWidgetPersona();
    const responses = useFirestoreGeneral('QuestionnairesResponses', 'Persona', personaId || '');
    const answeredIds = useMemo(
        () => (personaId && Array.isArray(responses)
            ? new Set(responses.map(r => r.ResearchID).filter(Boolean))
            : null),
        [personaId, responses]
    );
    const choices = useMemo(
        () => orderResearchChoices(control.options.choices || [], answeredIds),
        [control.options.choices, answeredIds]
    );
    return <SelectControlRenderer control={control} value={value} onChange={onChange} choices={choices} />;
};

const scoreFormat = new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * @extends {BaseWidget}
 */
export class PersonaGrowthWidget extends BaseWidget {
    getName() { return 'Groei op metingen'; }
    getCategory() { return 'medewerkers'; }
    getRequiredModule() { return 'personas'; }
    getIcon() { return <TrendingUpIcon />; }

    getControls() {
        // De onderzoekkeuze, de periode en de vragenlijst per onderzoek van de
        // onderzoekwidget zelf — zelfde instellingen, dus de openbare link leest
        // ze ook zo (`researchId`, `byNestedId[fieldId].visible`).
        // AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
        const research = new ResearchWidget().controls;
        return {
            researchId: new PersonaResearchSelectControl({
                label: research.researchId.label,
                description: 'Onderzoeken met antwoorden van de deelnemer van dit rapport staan bovenaan.',
                defaultValue: research.researchId.defaultValue,
                options: research.researchId.options,
            }),
            period: research.period,
            byNestedId: new NestedItemControl({
                label: 'Per vraag',
                description: 'Kies per vraag of hij in deze rapportage staat.',
                parentSettingKey: 'researchId',
                noParentText: research.byNestedId.noParentText,
                emptyText: research.byNestedId.emptyText,
                nestedItemsResolver: research.byNestedId.nestedItemsResolver,
                children: { visible: research.byNestedId.children.visible },
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * De score van één deelnemer per meetmoment voor één vraag.
 *
 * @param {object} props
 * @param {object} props.field - QuestionnaireFields-document (een matrixvraag)
 * @param {string} props.researchId - Research `ID`
 * @param {string} props.personaId - PersonaID van het rapport
 * @param {Date|null} props.startDate - Begin van de periode
 * @param {Date|null} props.endDate - Eind van de periode
 * @param {object|null} props.templateData - In een template: het antwoord van exportTemplateData
 * @returns {JSX.Element}
 */
const GrowthScores = ({ field, researchId, personaId, startDate, endDate, templateData }) => {
    const { data, rowDefs, loading, error } = useMatrixData({
        field: field.ID,
        researchId,
        personaId,
        startDate,
        endDate,
        templateData,
    });

    if (error) return <p className="output-widget-error">De scores konden niet geladen worden.</p>;
    if (loading) return <LoadingSpinner />;
    if (!data.length) return <p className="persona-growth-note">Nog geen antwoorden van deze deelnemer op deze vraag.</p>;

    // Bij een meetstandaardvraag is rowDefs één rij 'Gemiddelde' (de score van
    // het effect); anders één rij per stelling. Eén rij: geen rijnamenkolom,
    // anders staat er een leeg vak vooraan.
    // AI-CONTEXT: See docs/pitfalls.md#ui-msi-matrix-rowdefs-collapse
    const showRowTitles = rowDefs.length > 1;

    return (
        <table className="persona-growth-table">
            <thead>
                <tr>
                    {showRowTitles && <th>{''}</th>}
                    {data.map(moment => <th key={moment.name}>{moment.name}</th>)}
                </tr>
            </thead>
            <tbody>
                {rowDefs.map(row => (
                    <tr key={row.ID || row.Title}>
                        {showRowTitles && <th scope="row">{row.Title}</th>}
                        {data.map(moment => {
                            const value = moment[row.Title]?.value;
                            return (
                                <td key={moment.name} className="persona-growth-score">
                                    {typeof value === 'number' ? scoreFormat.format(value) : '—'}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

/**
 * @param {object} props
 * @param {string} [props.researchId] - Gekozen onderzoek (Research `ID`)
 * @param {Object<string, {visible?: boolean}>} [props.byNestedId] - Per vraag: `visible: false` laat hem weg
 * @param {{mode?: string, start?: string|null, end?: string|null}} [props.period] - Periode van de widget (PeriodOverrideControl)
 * @returns {JSX.Element}
 */
const Renderer = ({ researchId, byNestedId, period }) => {
    const { personasModuleOn, personaId, templateStatus } = usePublicPersona();
    const { templateData } = useContext(TemplateDataContext);
    const { startDate, endDate } = useWidgetPeriod(period);

    // In een template komen onderzoek, vragen en antwoorden uit de templategegevens,
    // net als bij de onderzoekwidget (docs/features/livereport-templates-deelnemer.md, T3).
    const inTemplate = templateStatus !== null;
    const active = !inTemplate && personasModuleOn && !!personaId;
    const researches = useFirestoreGeneral('Research', 'ID', active ? (researchId || '') : '');
    const research = inTemplate
        ? (templateData?.data?.research || []).find(r => r.ID === researchId) || null
        : (Array.isArray(researches) ? researches[0] : null);
    const firestoreFields = useFirestoreGeneralOrderBy(
        'QuestionnaireFields',
        'QuestionnaireID',
        inTemplate ? '' : (research?.QuestionnaireID || ''),
        'Position',
        'asc'
    );
    const liveFields = useMemo(() => (inTemplate
        ? (templateData?.data?.effects?.indicators || [])
            .filter(f => research && f.QuestionnaireID === research.QuestionnaireID)
            .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0))
        : firestoreFields), [inTemplate, templateData, research, firestoreFields]);

    // Een string als sleutel: de widget krijgt elke render een nieuw `byNestedId`.
    // AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
    const hiddenKey = Object.entries(byNestedId || {})
        .filter(([, item]) => item?.visible === false)
        .map(([id]) => id)
        .sort()
        .join('|');
    const fields = useMemo(() => {
        const hidden = new Set(hiddenKey.split('|'));
        return Array.isArray(liveFields)
            ? liveFields.filter(isQuestionField).filter(f => !hidden.has(f.ID))
            : null;
    }, [liveFields, hiddenKey]);

    const notice = templatePersonaNotice(templateStatus);
    if (notice) return notice;
    if (!personasModuleOn) return <ModulePlaceholder mode="disabled" moduleLabel="Deelnemers" />;
    if (!personaId) {
        return <p className="persona-growth-note">{NO_PERSONA_TEXT}</p>;
    }
    if (!researchId) return <WidgetSourcePlaceholder ctaLabel="Selecteer onderzoek" />;
    if (!inTemplate && !Array.isArray(researches)) return <LoadingSpinner />;
    if (!research) return <WidgetSourcePlaceholder ctaLabel="Selecteer onderzoek" />;
    if (!fields) return <LoadingSpinner />;
    if (!fields.length) return <p className="persona-growth-note">Geen vragen gevonden voor dit onderzoek.</p>;

    return (
        <div className="persona-growth-widget">
            {fields.map(field => (
                <section key={field.ID} className="persona-growth-question">
                    <ResearchFieldHeading field={field} />
                    {supportsTotalGrowth(field.Type)
                        ? (
                            <GrowthScores
                                field={field}
                                researchId={researchId}
                                personaId={personaId}
                                startDate={startDate}
                                endDate={endDate}
                                templateData={inTemplate ? templateData : null}
                            />
                        )
                        : <p className="persona-growth-note">{NOT_SCALE_NOTE}</p>}
                </section>
            ))}
        </div>
    );
};

export default PersonaGrowthWidget;
